import { useState } from 'react';
import { Box, Stack, Group, Badge, Avatar, TextInput, Select, Timeline } from '@mantine/core';
import { IconSearch, IconPlus, IconPhone, IconMail, IconUsers, IconNote, IconChecklist } from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockActivities, Activity } from '../mockData/crm';

const formatDateTime = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'call': return IconPhone;
    case 'email': return IconMail;
    case 'meeting': return IconUsers;
    case 'note': return IconNote;
    case 'task': return IconChecklist;
    default: return IconNote;
  }
};

const getActivityColor = (type: Activity['type']) => {
  switch (type) {
    case 'call': return 'green';
    case 'email': return 'blue';
    case 'meeting': return 'violet';
    case 'note': return 'gray';
    case 'task': return 'orange';
    default: return 'gray';
  }
};

export default function ActivitiesPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const types = ['call', 'email', 'meeting', 'note', 'task'];

  const filteredActivities = mockActivities.filter(activity => {
    const matchesSearch = activity.title.toLowerCase().includes(search.toLowerCase()) ||
                          activity.description.toLowerCase().includes(search.toLowerCase()) ||
                          activity.contactName.toLowerCase().includes(search.toLowerCase());
    const matchesType = !typeFilter || activity.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Sort by date descending
  const sortedActivities = [...filteredActivities].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Activities</Title>
          <Text c="dimmed" size="sm">{filteredActivities.length} activities</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Log Activity</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search activities..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Type"
            data={types.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Activities Timeline */}
      <Card>
        <Timeline active={sortedActivities.length} bulletSize={32} lineWidth={2}>
          {sortedActivities.map(activity => {
            const Icon = getActivityIcon(activity.type);
            const color = getActivityColor(activity.type);

            return (
              <Timeline.Item
                key={activity.id}
                bullet={<Icon size={16} />}
                color={color}
              >
                <Card p="sm" withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Group gap="sm">
                        <Text size="sm" fw={500}>{activity.title}</Text>
                        <Badge size="xs" color={color}>{activity.type}</Badge>
                      </Group>
                      <Text size="xs" c="dimmed">{formatDateTime(activity.createdAt)}</Text>
                    </Group>

                    <Text size="sm" c="dimmed">{activity.description}</Text>

                    <Group justify="space-between">
                      <Group gap="sm">
                        <Avatar size="xs" radius="xl" color="blue">
                          {activity.contactName.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Text size="xs">{activity.contactName}</Text>
                      </Group>
                      <Text size="xs" c="dimmed">by {activity.userName}</Text>
                    </Group>

                    {activity.completedAt && (
                      <Badge size="xs" variant="light" color="green">
                        Completed at {formatDateTime(activity.completedAt)}
                      </Badge>
                    )}
                  </Stack>
                </Card>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Card>
    </Stack>
  );
}
